import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, type MessagePayload } from 'firebase/messaging';
import { notificationService } from './api';
import browserNotificationService from './browserNotificationService';

const requiredEnvKeys = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
  'REACT_APP_FIREBASE_VAPID_KEY',
] as const;

const getFirebaseConfig = () => ({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
});

const hasRequiredConfig = () =>
  requiredEnvKeys.every((key) => Boolean(process.env[key]));

const buildServiceWorkerQuery = () => {
  const config = getFirebaseConfig();
  const query = new URLSearchParams();
  Object.entries(config).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  return query.toString();
};

let foregroundListenerInitialized = false;

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

export const initAdminFcm = async () => {
  try {
    console.log('[FCM_DEBUG] initAdminFcm started');
    if (!hasRequiredConfig()) {
      console.warn(
        '[FCM] Missing Firebase environment variables:',
        requiredEnvKeys.filter((key) => !process.env[key])
      );
      console.warn('[FCM_DEBUG] Missing config, aborting init');
      return;
    }

    console.log('[FCM_DEBUG] Firebase env present');
    const supported = await isSupported();
    console.log('[FCM_DEBUG] isSupported:', supported);
    if (!supported) {
      console.warn('[FCM] Firebase messaging is not supported in this browser');
      return;
    }

    const permissionGranted = await requestNotificationPermission();
    console.log('[FCM_DEBUG] Notification permission:', Notification.permission, 'granted:', permissionGranted);
    if (!permissionGranted) return;

    const firebaseApp = getApps().length ? getApps()[0] : initializeApp(getFirebaseConfig());
    console.log('[FCM_DEBUG] Firebase app initialized');
    const messaging = getMessaging(firebaseApp);

    const swQuery = buildServiceWorkerQuery();
    console.log('[FCM_DEBUG] Registering service worker with query params');
    const serviceWorkerRegistration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js${swQuery ? `?${swQuery}` : ''}`
    );
    console.log('[FCM_DEBUG] Service worker registered:', serviceWorkerRegistration.scope);

    const fcmToken = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration,
    });
    console.log('[FCM_DEBUG] getToken result:', fcmToken ? `${fcmToken.slice(0, 20)}...` : 'null');

    if (fcmToken) {
      const tokenRegisterResponse = await notificationService.registerDeviceToken(fcmToken, 'web');
      console.log('[FCM_DEBUG] registerDeviceToken response:', tokenRegisterResponse?.data);
    } else {
      console.warn('[FCM_DEBUG] No FCM token received');
    }

    if (!foregroundListenerInitialized) {
      console.log('[FCM_DEBUG] Registering foreground onMessage listener');
      onMessage(messaging, (payload: MessagePayload) => {
        console.log('[FCM_DEBUG] Foreground message payload:', payload);
        const title = payload.notification?.title || 'Notification';
        const body = payload.notification?.body || '';
        const url = payload.data?.url || '/notifications/history';
        window.dispatchEvent(new Event('admin:fcm-message'));

        browserNotificationService.showNotification(title, {
          body,
          data: {
            url: `${window.location.origin}${url}`,
          },
        });
      });
      foregroundListenerInitialized = true;
    }
  } catch (error) {
    console.error('[FCM] Failed to initialize admin FCM', error);
    console.error('[FCM_DEBUG] initAdminFcm error details:', error);
  }
};
